import type { Theme, SxProps } from '@mui/material/styles';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { SignInButton } from 'src/layouts/components/sign-in-button';

import { Logo as LogoBase } from 'src/components/logo';
import { Scrollbar as ScrollbarBase } from 'src/components/scrollbar';

import { useAuthContext } from 'src/auth/hooks/use-auth-context';

import { Nav, NavUl } from '../components';
import { NavList } from './nav-mobile-list';

import type { NavMobileItemData } from './nav-mobile-list';

// ----------------------------------------------------------------------

// Logo·Scrollbar 는 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const Logo = LogoBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  href?: string;
  isSingle?: boolean;
  disabled?: boolean;
  className?: string;
}>;

const Scrollbar = ScrollbarBase as unknown as React.ComponentType<{
  sx?: SxProps<Theme>;
  fillContent?: boolean;
  children?: React.ReactNode;
  className?: string;
}>;

type NavMobileProps = {
  data: NavMobileItemData[];
  open?: boolean;
  onClose: () => void;
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  sx?: SxProps<Theme>;
  user?: unknown;
  loading?: unknown;
  onDemoExploreClick?: () => void;
};

export function NavMobile({
  data,
  open,
  onClose,
  slots,
  sx,
  user,
  loading,
  onDemoExploreClick,
}: NavMobileProps) {
  const pathname = usePathname();

  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const router = useRouter();
  const { authenticated, loading: authLoading } = useAuthContext();

  return (
    <Drawer
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: [
            {
              display: 'flex',
              flexDirection: 'column',
              width: 'var(--layout-nav-mobile-width)',
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ],
        },
      }}
    >
      {slots?.topArea ?? (
        <Box
          sx={{
            pt: 3,
            pb: 2,
            pl: 2.5,
            display: 'flex',
          }}
        >
          <Logo />
        </Box>
      )}

      <Scrollbar fillContent>
        <Nav
          sx={{
            pb: 3,
            // 🔑 줄의 좌우 여백은 **서비스 왼쪽 메뉴와 같은 값**이다(`layouts/service/layout.tsx` 의 `navSlots`)
            px: 2,
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
          }}
        >
          <NavUl>
            {data.map((list: NavMobileItemData) => (
              <NavList
                key={list.title}
                data={list}
                onClose={onClose}
                onDemoExploreClick={onDemoExploreClick}
              />
            ))}
          </NavUl>
        </Nav>
      </Scrollbar>

      {slots?.bottomArea ?? (
        <Box
          sx={{
            py: 3,
            px: 2.5,
            gap: 1.5,
            display: 'flex',
          }}
        >
          {/* 로그인 전 — 「50% 할인받기」(요금 화면 · 1+1 구독) · 「로그인」 / 로그인 뒤 — 「워크스페이스」 · 「도입 문의」 · 인증이 풀리기 전엔 비운다(단추가 바뀌며 깜빡이지 않게) */}
          {authLoading ? null : authenticated ? (
            <Stack width='100%' spacing={1}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  router.push(paths.service.root);
                }}
              >
                워크스페이스
              </Button>
              <Button
                fullWidth
                variant="outlined"
                rel="noopener"
                target="_blank"
                href='https://catchmoments.channel.io'
              >
                도입 문의
              </Button>
            </Stack>
          ) : (
            <Stack width='100%' spacing={1}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  router.push(paths.subscription.plan);
                }}
              >
                50% 할인받기
              </Button>
              <SignInButton fullWidth variant="outlined" color="inherit" />
            </Stack>
          )}
        </Box>
      )}
    </Drawer>
  );
}
